import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { initMercadoPago, Wallet } from '@mercadopago/sdk-react';
import { toast } from 'sonner';
import { ShieldCheck, ArrowLeft, CreditCard } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { usePageTitle } from '../hooks/usePageTitle';
import { currentUnitPrice } from '../utils/pricing';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

let mpInitialized = false;

const Checkout = () => {
  usePageTitle('Pagamento');
  const { user, isAuthenticated, loading: authLoading } = useAuth();
  const { cart } = useCart();
  const navigate = useNavigate();

  const [config, setConfig] = useState(null);
  const [products, setProducts] = useState([]);
  const [customer, setCustomer] = useState({
    name: '',
    email: '',
    phone: '',
    cpf: '',
  });
  const [step, setStep] = useState('form');
  const [preferenceId, setPreferenceId] = useState('');
  const [loading, setLoading] = useState(false);

  // useMemo depois dos useState — evita ReferenceError (Temporal Dead Zone) que gerava TELA BRANCA.
  const walletInitialization = useMemo(() => ({ preferenceId }), [preferenceId]);
  const walletCustomization = useMemo(() => ({ texts: { valueProp: 'smart_option' } }), []);

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) {
      navigate('/login?next=/checkout');
      return;
    }
    setCustomer((c) => ({
      ...c,
      name: c.name || user?.name || '',
      email: c.email || user?.email || '',
    }));
    fetchPaymentConfig();
    loadCartProducts();
  }, [authLoading, isAuthenticated, cart]); // eslint-disable-line react-hooks/exhaustive-deps -- só re-executar quando estes mudam

  const fetchPaymentConfig = async () => {
    try {
      const res = await axios.get(`${API_URL}/payments/config`);
      setConfig(res.data);
      if (res.data.configured && res.data.public_key && !mpInitialized) {
        initMercadoPago(res.data.public_key, { locale: 'pt-BR' });
        mpInitialized = true;
      }
    } catch (e) {
      console.error(e);
    }
  };

  const loadCartProducts = async () => {
    if (!cart?.items || cart.items.length === 0) {
      setProducts([]);
      return;
    }
    try {
      const ids = cart.items.map((i) => i.product_id);
      const res = await axios.post(`${API_URL}/products/batch`, ids);
      const items = res.data.map((p) => {
        const ci = cart.items.find((i) => i.product_id === p.id);
        return {
          ...p,
          quantity: ci.quantity,
          displayPrice: currentUnitPrice(p, ci),
        };
      });
      setProducts(items);
    } catch (e) {
      console.error(e);
    }
  };

  const total = products.reduce((s, p) => s + p.displayPrice * p.quantity, 0);

  const proceedToPayment = async () => {
    if (!customer.name.trim() || !customer.email.trim()) {
      toast.error('Preencha nome e e-mail para continuar');
      return;
    }
    if (products.length === 0) {
      toast.error('Seu carrinho está vazio');
      return;
    }
    setLoading(true);
    try {
      const payload = {
        items: products.map((p) => ({
          title: p.name,
          quantity: p.quantity,
          unit_price: Number(p.displayPrice),
        })),
        customer_name: customer.name,
        customer_email: customer.email,
        customer_phone: customer.phone || null,
      };
      const res = await axios.post(`${API_URL}/payments/checkout/cart`, payload);
      setPreferenceId(res.data.preference_id);
      setStep('payment');
    } catch (e) {
      const msg = e.response?.data?.detail || 'Erro ao iniciar pagamento';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  if (authLoading) return null;

  return (
    <div data-testid="checkout-page" className="min-h-screen bg-white pt-32 pb-24 px-6 md:px-12 lg:px-24">
      <div className="max-w-6xl mx-auto">
        <button
          onClick={() => step === 'payment' ? setStep('form') : navigate('/carrinho')}
          className="inline-flex items-center gap-2 font-accent text-[11px] tracking-[0.3em] uppercase text-neutral-500 hover:text-neutral-900 transition-colors mb-10"
          data-testid="back-to-cart"
        >
          <ArrowLeft className="w-4 h-4" strokeWidth={1.5} />
          {step === 'payment' ? 'Voltar aos dados' : 'Voltar ao Carrinho'}
        </button>

        <h1 className="font-heading text-4xl md:text-5xl font-light tracking-tight text-neutral-900 mb-3">
          Finalizar Pagamento
        </h1>
        <p className="text-sm font-light text-neutral-500 mb-12">
          Pagamento seguro processado pelo Mercado Pago · cartão, Pix ou boleto
        </p>

        {config && !config.configured && (
          <div
            data-testid="mp-not-configured"
            className="border border-amber-300 bg-amber-50 text-amber-900 px-6 py-4 mb-10 text-sm"
          >
            Pagamentos online ainda estão sendo configurados pela loja.
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          <div className="lg:col-span-2">
            {step === 'form' && (
              <section>
                <h2 className="font-accent text-[11px] tracking-[0.3em] uppercase text-neutral-500 mb-6">
                  1 · Seus dados
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10">
                  <div>
                    <label className="text-xs text-neutral-500 mb-1 block">Nome completo *</label>
                    <input
                      data-testid="checkout-name"
                      type="text"
                      value={customer.name}
                      onChange={(e) => setCustomer({ ...customer, name: e.target.value })}
                      className="w-full border-b border-neutral-300 focus:border-neutral-900 outline-none py-2 bg-transparent"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-neutral-500 mb-1 block">E-mail *</label>
                    <input
                      data-testid="checkout-email"
                      type="email"
                      value={customer.email}
                      onChange={(e) => setCustomer({ ...customer, email: e.target.value })}
                      className="w-full border-b border-neutral-300 focus:border-neutral-900 outline-none py-2 bg-transparent"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-neutral-500 mb-1 block">Telefone</label>
                    <input
                      data-testid="checkout-phone"
                      type="tel"
                      value={customer.phone}
                      onChange={(e) => setCustomer({ ...customer, phone: e.target.value })}
                      className="w-full border-b border-neutral-300 focus:border-neutral-900 outline-none py-2 bg-transparent"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-neutral-500 mb-1 block">CPF</label>
                    <input
                      data-testid="checkout-cpf"
                      type="text"
                      value={customer.cpf}
                      onChange={(e) => setCustomer({ ...customer, cpf: e.target.value })}
                      className="w-full border-b border-neutral-300 focus:border-neutral-900 outline-none py-2 bg-transparent"
                    />
                  </div>
                </div>

                <h2 className="font-accent text-[11px] tracking-[0.3em] uppercase text-neutral-500 mb-6">
                  Itens do pedido
                </h2>
                {products.length === 0 ? (
                  <p className="text-sm text-neutral-500">Seu carrinho está vazio.</p>
                ) : (
                  <ul className="divide-y divide-neutral-200 mb-10">
                    {products.map((p) => (
                      <li key={p.id} className="py-4 flex items-center gap-4">
                        <img src={p.images?.[0] || '/placeholder.svg'} alt={p.name} className="w-16 h-16 object-cover" />
                        <div className="flex-1">
                          <p className="text-sm font-medium text-neutral-900">{p.name}</p>
                          <p className="text-xs text-neutral-500">Qtd. {p.quantity}</p>
                        </div>
                        <span className="text-sm text-neutral-900">
                          R$ {(p.displayPrice * p.quantity).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            )}

            {step === 'payment' && (
              <section>
                <h2 className="font-accent text-[11px] tracking-[0.3em] uppercase text-neutral-500 mb-6">
                  2 · Escolha como pagar
                </h2>
                <div className="border border-neutral-200 p-8 mb-6">
                  <p className="text-sm font-light text-neutral-700 mb-6">
                    Clique abaixo para concluir sua compra com segurança pelo Mercado Pago. Aceitamos <strong>cartão de crédito, débito, Pix</strong> e boleto.
                  </p>
                  <div data-testid="mp-wallet-container">
                    <Wallet
                      initialization={walletInitialization}
                      customization={walletCustomization}
                    />
                  </div>
                  <div className="mt-6 flex items-center gap-2 text-xs text-neutral-500">
                    <ShieldCheck className="w-4 h-4" strokeWidth={1.5} />
                    Você será direcionada(o) ao ambiente seguro do Mercado Pago
                  </div>
                </div>
              </section>
            )}
          </div>

          <div className="lg:col-span-1">
            <div className="border border-neutral-200 p-8 sticky top-32">
              <h2 className="font-accent text-[11px] tracking-[0.3em] uppercase text-neutral-500 mb-6">
                Total a pagar
              </h2>
              <p data-testid="checkout-total" className="font-heading text-3xl text-neutral-900 mb-8">
                R$ {total.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
              </p>

              {step === 'form' && (
                <button
                  data-testid="start-payment-button"
                  onClick={proceedToPayment}
                  disabled={loading || !config?.configured || products.length === 0}
                  className="w-full bg-neutral-900 text-white px-8 py-4 uppercase tracking-widest text-xs hover:bg-black disabled:opacity-40 disabled:cursor-not-allowed transition-all duration-300 flex items-center justify-center gap-2"
                >
                  <CreditCard className="w-4 h-4" strokeWidth={1.5} />
                  {loading ? 'Preparando...' : 'Continuar para pagamento'}
                </button>
              )}
              {step === 'payment' && (
                <p className="text-xs text-neutral-500">
                  Clique no botão do Mercado Pago para concluir sua compra.
                </p>
              )}

              <div className="mt-6 flex items-center gap-2 text-xs text-neutral-500">
                <ShieldCheck className="w-4 h-4" strokeWidth={1.5} />
                Ambiente seguro Mercado Pago
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Checkout;
